import { Link, useLocation } from '@tanstack/react-router'
import { Menu, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ThemeToggle } from '@/components/theme-toggle'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { getLocaleFromPath, withLocalePath } from '@/lib/i18n-utils'

type Entry = { label: string; href: string }

/**
 * The register's masthead. Navigation in the Public Ledger world is a row of
 * ruled column heads, not pills: hairline rules, plain text, one action.
 */
export function LedgerNav() {
  const { t } = useTranslation('ledger')
  const location = useLocation()
  const locale = getLocaleFromPath(location.pathname)
  const [open, setOpen] = useState(false)
  useEffect(() => setOpen(false), [location.pathname])

  const href = (path: string) => withLocalePath(path, locale)

  const entries: Array<Entry> = [
    { label: t('nav.about'), href: '/about' },
    { label: t('nav.engineering'), href: '/what-we-do' },
    { label: t('nav.cloud'), href: '/cloud' },
    { label: t('nav.software'), href: '/products' },
    { label: t('nav.research'), href: '/research' },
    { label: t('nav.community'), href: '/community' },
    { label: t('nav.blog'), href: '/blog' },
  ]

  return (
    <header className="ledger-ground sticky top-0 z-50 border-b border-[var(--rule)]">
      <div className="ledger-wrap">
        <div className="flex h-14 items-center justify-between gap-4">
          <Link
            to={href('/')}
            className="ledger-focus flex items-center gap-2.5 whitespace-nowrap"
          >
            <img
              src="/logo-64.webp"
              alt=""
              aria-hidden="true"
              className="h-7 w-7 object-contain"
              width={28}
              height={28}
              decoding="async"
            />
            <span
              className="text-[1.05rem] font-extrabold tracking-tight"
              style={{ fontFamily: 'var(--ledger-sans)' }}
            >
              Horizon Tech
            </span>
          </Link>

          <nav
            aria-label={t('nav.main')}
            className="hidden items-center gap-4 xl:gap-6 lg:flex"
          >
            {entries.map((entry) => (
              <Link
                key={entry.href}
                to={href(entry.href)}
                className="ledger-focus text-[0.8125rem] font-semibold text-[var(--ink-2)] transition-colors hover:text-[var(--stamp)]"
                style={{ fontFamily: 'var(--ledger-sans)' }}
              >
                {entry.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-0.5">
              <LanguageSwitcher />
              <ThemeToggle />
            </div>
            <span className="hidden lg:block">
              <Link
                to={href('/contact')}
                className="ledger-button ledger-focus whitespace-nowrap"
              >
                {t('nav.demo')}
              </Link>
            </span>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="ledger-focus inline-flex h-9 w-9 items-center justify-center rounded border border-[var(--rule)] lg:hidden"
              aria-expanded={open}
              aria-label={open ? t('nav.closeMenu') : t('nav.openMenu')}
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="border-t border-[var(--rule)] lg:hidden">
          <div className="ledger-wrap py-3">
            <nav className="flex flex-col">
              {[...entries, { label: t('nav.demo'), href: '/contact' }].map(
                (entry) => (
                  <Link
                    key={entry.href}
                    to={href(entry.href)}
                    onClick={() => setOpen(false)}
                    className="ledger-focus border-b border-[var(--rule)] py-3 text-[0.9375rem] font-semibold text-[var(--ink)] last:border-b-0"
                    style={{ fontFamily: 'var(--ledger-sans)' }}
                  >
                    {entry.label}
                  </Link>
                ),
              )}
            </nav>
          </div>
        </div>
      )}
    </header>
  )
}
